import { sendGTMEvent } from "@next/third-parties/google"
import { useQuery } from "@tanstack/react-query"

import { IDVerificationStatus, LoanApplicationState } from "@lib/clients/nelo-api-client"
import { neloApiClient } from "@lib/config-client"

const LOAN_APPLICATION_FINAL_STATES = [
  LoanApplicationState.APPROVED,
  LoanApplicationState.REJECTED,
  LoanApplicationState.NEEDS_MANUAL_REVIEW,
  LoanApplicationState.SUBMITTED,
  LoanApplicationState.APPROVED_BY_MODEL,
  LoanApplicationState.APPROVED_DP_REQUIRED,
]

export const useIdentityVerificationStatus = (authToken: string | null) => {
  return useQuery({
    queryKey: ["identityVerificationStatus", authToken],
    queryFn: async () => {
      const response = await neloApiClient.checkIdentityVerificationStatus()
      return response.verificationStatus
    },
    enabled: !!authToken,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: ({ state }) => {
      const { data } = state ?? {}

      if (!data) {
        return false
      }

      const terminalStates = [
        IDVerificationStatus.VERIFIED,
        IDVerificationStatus.REJECTED,
        IDVerificationStatus.EXPIRED,
        IDVerificationStatus.DOCUMENT_MISMATCH,
        IDVerificationStatus.FRAUD_REVIEW,
      ]

      return terminalStates.includes(data) ? false : 5000
    },
  })
}

export const useLoanApplicationStatus = (authToken: string | null) => {
  return useQuery({
    queryKey: ["loanApplicationStatus", authToken],
    queryFn: async () => {
      const response = await neloApiClient.checkLoanApplicationStatus()
      return response.state
    },
    enabled: !!authToken,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: ({ state }) => {
      const { data } = state ?? {}

      if (!data) {
        return false
      }

      const isInFinalState = LOAN_APPLICATION_FINAL_STATES.includes(data)

      if (isInFinalState && data === LoanApplicationState.APPROVED) {
        sendGTMEvent({
          event: "loan_application_approved",
        })
      }

      return isInFinalState ? false : 5000
    },
  })
}
